import { ButtonLegacy } from '@/components/ui/button-legacy'
import { PlusIcon } from '@/components/ui/icons'
import { DeallocateImageButton } from '@/features/photoset/components/deallocate-image-button'
import {
	SpaceCardEmptyPreviewSlot,
	SpaceCardHeader,
	SpaceCardImagePreviewSlot,
	SpaceCardImagesPreviewGrid,
	SpaceCardWrapper
} from '@/features/photoset/components/space-card-atoms'
import { usePhotosetStore } from '@/features/photoset/store/store'
import type { OnAllocateAction } from '@/features/photoset/types'
import {
	spaceNameToLabel,
	spaceNameToShortcut
} from '@/features/photoset/utils'
import { objectValues } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

export const GalleryAllocationSidebar = ({
	onAllocate
}: {
	onAllocate: (values: OnAllocateAction) => void
}) => {
	const { t } = useTranslation()
	const images = usePhotosetStore((store) => store.images)
	const photosetSpacesDict = usePhotosetStore(
		(store) => store.photosetSpacesDict
	)
	const selectedImages = usePhotosetStore((store) => store.selectedImages)

	return (
		<div className='flex h-full flex-col items-stretch gap-8 overflow-y-auto py-6 pr-2 pl-6'>
			{objectValues(photosetSpacesDict).map((photosetSpace) => {
				const spaceImages = [...images.values()].filter(
					(image) => image.space?.id === photosetSpace.space.id
				)
				const imagesInPreview = spaceImages?.slice(-5).reverse() ?? []

				return (
					<SpaceCardWrapper
						hasImages={spaceImages.length >= 1}
						key={photosetSpace.space.key_name}
					>
						<SpaceCardHeader
							headerTitle={
								spaceNameToLabel[photosetSpace.space.key_name]
							}
							images={spaceImages}
							shortcut={
								spaceNameToShortcut[
									photosetSpace.space.key_name
								]
							}
						/>
						{spaceImages.length >= 1 ? (
							<div className='flex items-stretch gap-2'>
								<ButtonLegacy
									className='w-full'
									onClick={() =>
										onAllocate({
											photosetSpaceId: photosetSpace.id,
											imagesIds: selectedImages,
											trackInHistory: true,
											isShortcut: false,
											keepSelected: false
										})
									}
									shape='square'
									size='lg'
									type='button'
									variant='secondary'
								>
									<PlusIcon size={20} />
								</ButtonLegacy>

								<SpaceCardImagesPreviewGrid>
									{imagesInPreview.map((image) => {
										const otherImagesCount =
											spaceImages.length -
											imagesInPreview.length
										return (
											<SpaceCardImagePreviewSlot
												image={image}
												key={image.id}
												otherImagesCount={
													otherImagesCount
												}
												overlayComponent={
													<DeallocateImageButton
														image={image}
														keepSelected={true}
														photosetSpaceId={
															photosetSpace.id
														}
														showToast={false}
														trackInHistory={true}
													/>
												}
											/>
										)
									})}

									{Array.from(
										{
											length: 5 - imagesInPreview.length
										},
										(_, i) => `empty-slot-${i}`
									).map((key) => (
										<SpaceCardEmptyPreviewSlot key={key} />
									))}
								</SpaceCardImagesPreviewGrid>
							</div>
						) : (
							<ButtonLegacy
								className='w-full py-[22px]'
								onClick={() =>
									onAllocate({
										photosetSpaceId: photosetSpace.id,
										imagesIds: selectedImages,
										trackInHistory: true,
										isShortcut: false,
										keepSelected: false
									})
								}
								type='button'
								variant='secondary'
							>
								<PlusIcon size={20} />
								<h3>{t('photoset:allocation.place_here')}</h3>
							</ButtonLegacy>
						)}
					</SpaceCardWrapper>
				)
			})}
		</div>
	)
}
